import React, { useEffect, useState } from "react"
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Table,
  Button,
} from "reactstrap"
import { FaChevronLeft, FaChevronRight, FaEye } from "react-icons/fa6"
import Breadcrumb from "../components/Common/Breadcrumb"
import { Badge } from "antd"
import { MdCurrencyPound } from "react-icons/md"
import { fetchData } from "utils/Common_api_functions"
import moment from "moment"
import Loader from "components/Loader/loader"

const statusConfig = {
  Pending: {
    color: "red",
    text: "Pending",
  },
  succeeded: {
    color: "green", // reactstrap color
    text: "Success",
  },
  requires_payment_method: {
    color: "yellow",
    text: "In-Complete",
  },
}

const getStatusConfig = status =>
  statusConfig[status] || {
    color: "red",
    text: "Failed",
  }

const PaymentsIndex = () => {
  const [page, setPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [allPayments, setAllPayments] = useState([])
  const [payments, setPayments] = useState([])

  useEffect(() => {
    getPayments()
  }, [])

  async function getPayments() {
    const APIData = await fetchData(setLoading, "payments")
    if (APIData?.success) {
      setPayments(APIData?.data)
      setAllPayments(APIData?.data)
    }
  }

  const totalPages = Math.ceil(payments?.length / itemsPerPage)
  const finalData = payments?.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  )

  const changePage = pageNumber => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setPage(pageNumber)
      window.scrollTo(0, 0)
    }
  }

  const handleView = payment => {
    setSelectedPayment(payment)
    setModalOpen(true)
  }

  const toggleModal = () => {
    setModalOpen(!modalOpen)
    if (modalOpen) {
      setSelectedPayment(null)
    }
  }

  const [searchString, setSearchString] = useState("")

  useEffect(() => {
    const modifiedData = allPayments.map(item => ({
      ...item,
      subscription:
        item.subscription === "Upgrade Plan"
          ? "Global Upgrade"
          : item.subscription,
      planType:
        item.planType === "Upgrade Plan" ? "Global Upgrade" : item.planType,
    }))

    if (searchString === "") {
      setPayments(modifiedData)
    } else {
      const filteredData = modifiedData.filter(
        item =>
          item?.user?.name
            ?.toLowerCase()
            .includes(searchString.toLowerCase()) ||
          item?.transactionId
            ?.toLowerCase()
            .includes(searchString.toLowerCase()) ||
          item?.status?.toLowerCase().includes(searchString.toLowerCase()) ||
          item?.planType?.toLowerCase().includes(searchString.toLowerCase()) ||
          String(item?.amount).includes(searchString) ||
          item?.subscription?.toLowerCase().includes(searchString.toLowerCase())
      )

      setPayments(filteredData)
    }
  }, [searchString, allPayments])

  const maxVisiblePages = 5

  const startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2))

  const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

  const visiblePages = Array.from(
    { length: endPage - startPage + 1 },
    (_, i) => startPage + i
  )

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="" breadcrumbItem="Payments" />
        {loading && <Loader />}
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="text-black fw-bold ps-1 mb-0">Payments</h4>
                  <div className="form-group">
                    <Input
                      placeholder="Search Payments"
                      value={searchString}
                      onChange={e => setSearchString(e.target.value)}
                    />
                  </div>
                </div>

                <div className="mt-3">
                  <Table responsive bordered className="rounded-3">
                    <thead className="table-primary text-dark">
                      <tr>
                        <th>#</th>
                        <th>Athlete Name</th>
                        <th>Plan Name</th>
                        <th>Plan Type</th>
                        <th>Amount</th>
                        <th>Date</th>
                        <th>Transaction ID</th>
                        <th style={{ width: "120px" }}>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {finalData.map((payment, index) => {
                        const { color, text } = getStatusConfig(payment.status)
                        return (
                          <tr key={payment.id}>
                            <td>{index + 1}</td>
                            <td>{payment?.user?.name}</td>
                            <td>{payment?.subscription}</td>
                            <td>{payment?.planType}</td>
                            <td>
                              <MdCurrencyPound />
                              {payment.amount.toFixed(2)}
                            </td>
                            <td>
                              {moment(payment.createdAt).format("DD-MM-YYYY")}
                            </td>
                            <td>{payment.transactionId || "-"}</td>
                            <td>
                              <Badge color={color} text={text} />
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-info"
                                onClick={() => handleView(payment)}
                              >
                                <FaEye size={12} />
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-between align-items-center my-2 px-2">
                  <p className="mb-0">
                    Showing {finalData.length} out of {payments.length}{" "}
                    payments.
                  </p>
                  <div className="d-flex flex-wrap">
                    <div className="d-flex flex-wrap">
                      {/* Prev */}
                      <div
                        className="bd-number-pagination me-1"
                        onClick={() => changePage(page - 1)}
                        style={{
                          cursor: "pointer",
                          opacity: page === 1 ? 0.5 : 1,
                        }}
                      >
                        <FaChevronLeft />
                      </div>

                      {/* Page Numbers (5 only) */}
                      {visiblePages.map(p => (
                        <div
                          key={p}
                          className={`bd-number-pagination me-1 ${
                            page === p ? "activepage" : ""
                          }`}
                          onClick={() => changePage(p)}
                          style={{ cursor: "pointer" }}
                        >
                          {p < 10 ? `0${p}` : p}
                        </div>
                      ))}

                      {/* Next */}
                      <div
                        className="bd-number-pagination"
                        onClick={() => changePage(page + 1)}
                        style={{
                          cursor: "pointer",
                          opacity: page === totalPages ? 0.5 : 1,
                        }}
                      >
                        <FaChevronRight />
                      </div>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Modal */}
        {selectedPayment &&
          (() => {
            const { color, text } = getStatusConfig(selectedPayment.status)

            return (
              <Modal isOpen={modalOpen} toggle={toggleModal} centered>
                <ModalHeader toggle={toggleModal}>Payment Details</ModalHeader>
                <ModalBody>
                  <Table borderless>
                    <tbody>
                      <tr>
                        <th>User Name:</th>
                        <td>{selectedPayment.user?.name}</td>
                      </tr>

                      <tr>
                        <th>Amount:</th>
                        <td>
                          <MdCurrencyPound />
                          {selectedPayment.amount.toFixed(2)}
                        </td>
                      </tr>

                      <tr>
                        <th>Date:</th>
                        <td>
                          {moment(selectedPayment.createdAt).format(
                            "DD-MM-YYYY"
                          )}
                        </td>
                      </tr>

                      <tr>
                        <th>Transaction ID:</th>
                        <td>{selectedPayment.transactionId || "-"}</td>
                      </tr>

                      <tr>
                        <th>Status:</th>
                        <td>
                          <Badge color={color} text={text} />
                        </td>
                      </tr>

                      <tr>
                        <th>Subscription Name:</th>
                        <td>{selectedPayment.subscription}</td>
                      </tr>

                      <tr>
                        <th>Plan Type:</th>
                        <td>{selectedPayment.planType}</td>
                      </tr>
                    </tbody>
                  </Table>
                </ModalBody>

                <ModalFooter>
                  <Button color="secondary" onClick={toggleModal}>
                    Close
                  </Button>
                </ModalFooter>
              </Modal>
            )
          })()}
      </Container>
    </div>
  )
}

export default PaymentsIndex
